import { useState, useEffect } from 'react';
import { NavLink, Outlet, Link } from 'react-router-dom';
import { useAuth } from '../../auth';
import { UserMenu } from '../../components/UserMenu';
import { Wordmark } from '../../components/Wordmark';

interface AdminStats {
  pendingApplications: number;
  totalProfiles: number;
  totalRooms: number;
  liveRooms: number;
}

const NAV_ITEMS = [
  { path: '/admin/lead-generation', label: 'Lead Finder' },
  { path: '/admin/applications', label: 'Applications', badge: 'pending' as const },
  { path: '/admin/user-support', label: 'User Support' },
  { path: '/admin/profiles', label: 'Profiles' },
  { path: '/admin/events', label: 'Events' },
  { path: '/admin/moderation', label: 'Moderation' },
  { path: '/admin/refunds', label: 'Refunds' },
  { path: '/admin/reference-packs', label: 'Reference Packs' },
  { path: '/admin/account-deletion', label: 'Account Deletion' },
  { path: '/admin/plans', label: 'Plans' },
  { path: '/admin/design-review', label: 'Design Review' },
  { path: '/admin/test-status', label: 'Test Status' },
  { path: '/admin/crm', label: 'Outreach CRM' },
];

export function AdminLayout() {
  const { getToken } = useAuth();
  const [stats, setStats] = useState<AdminStats | null>(null);

  useEffect(() => {
    async function fetchStats() {
      try {
        const token = await getToken();
        const response = await fetch('/api/admin/status', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data.stats);
        }
      } catch (error) {
        console.error('Error fetching admin stats:', error);
      }
    }
    fetchStats();
  }, [getToken]);

  return (
    <div className="min-h-screen flex bg-bg">
      {/* Sidebar */}
      <aside className="w-56 border-r border-line flex flex-col bg-panel">
        <div className="h-14 px-5 flex items-center border-b border-line">
          <Wordmark />
        </div>
        <div className="px-4 py-3">
          <div className="text-[10px] font-mono text-text-faint uppercase font-semibold">
            Admin
          </div>
        </div>
        <nav className="flex-1 px-2 space-y-0.5">
          {NAV_ITEMS.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-[13px] transition-colors ${
                  isActive
                    ? 'bg-accent/10 text-accent font-medium'
                    : 'text-text-dim hover:text-text hover:bg-panel2'
                }`
              }
            >
              <span>{item.label}</span>
              {item.badge === 'pending' && stats && stats.pendingApplications > 0 && (
                <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-warn/20 text-warn rounded">
                  {stats.pendingApplications}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-line">
          <Link to="/" className="text-[12px] text-text-dim hover:text-text transition-colors">
            Back to site
          </Link>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="h-14 px-6 border-b border-line flex items-center justify-between bg-panel shrink-0">
          <div className="flex items-center gap-4">
            {stats && (
              <div className="flex items-center gap-4 text-[12px] text-text-dim">
                <span>{stats.totalProfiles} profiles</span>
                <span className="text-line">|</span>
                <span>{stats.totalRooms} events</span>
                {stats.liveRooms > 0 && (
                  <>
                    <span className="text-line">|</span>
                    <span className="text-ok flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 bg-ok rounded-full animate-pulse" />
                      {stats.liveRooms} live
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
          <UserMenu />
        </header>
        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
